// ============================================================================
// Organization API — used by the org admin dashboard for self-enrollment
// code management. Backend routes (see backend/organizations/views.py +
// backend/organizations/serializers.py):
//
//   GET  /api/organizations/{id}/                  org details inc. self_enroll_code,
//                                                  enrolment_url, enrolled_count
//   POST /api/organizations/{id}/regenerate_code/  mint a fresh code
//
// Read scope: org admins see only their own org; platform admins see all.
// `regenerate_code` is allowed for the org's own admins (not just platform
// admins) so org admins can rotate their code without escalation.
// ============================================================================

import { api } from './client.js'
import { unwrapList } from '../utils/format.js'

export async function getOrganization(id) {
  const { data } = await api.get(`/api/organizations/${id}/`)
  return data
}

/**
 * List active organisations, optionally filtered by org_type. Used by
 * the doctor's patient-registration form to populate the cascading
 * "Which organization?" dropdown when a non-General category is
 * picked. Returns the unwrapped list.
 *
 * org_type values match Organization.ORG_TYPE_CHOICES:
 *   "company" | "government" | "institution" | "ngo" | "union" | "other"
 */
export async function listOrganizationsByType(orgType) {
  const params = { is_active: true, page_size: 200 }
  if (orgType) params.org_type = orgType
  const { data } = await api.get('/api/organizations/', { params })
  return unwrapList(data)
}

// ---- Centres (sub-sites under a parent organisation) ----

export async function listOrgCentres(orgId, { activeOnly = true } = {}) {
  const params = { organization: orgId, page_size: 100 }
  if (activeOnly) params.is_active = true
  const { data } = await api.get('/api/organizations/centres/', { params })
  return unwrapList(data)
}

export async function createOrgCentre(payload) {
  // payload: { organization, name, location, address, contact_*, manager, notes }
  const { data } = await api.post('/api/organizations/centres/', payload)
  return data
}

export async function updateOrgCentre(id, patch) {
  const { data } = await api.patch(`/api/organizations/centres/${id}/`, patch)
  return data
}

export async function regenerateOrgCentreCode(id) {
  const { data } = await api.post(
    `/api/organizations/centres/${id}/regenerate_code/`,
  )
  return data
}

export async function deactivateOrgCentre(id) {
  // DELETE soft-deactivates server-side (keeps staff-link history).
  const { data } = await api.delete(`/api/organizations/centres/${id}/`)
  return data
}

export async function regenerateOrgCode(id) {
  const { data } = await api.post(`/api/organizations/${id}/regenerate_code/`)
  return data
}

/**
 * Platform-admin only. Update the org's annual subscription fee — drives
 * the Value & Impact ROI denominator (pro-rated to the analytics window).
 * Pass `null` (or undefined) to clear the value back to the rendered-
 * services baseline. Server-side gate is the OrganizationViewSet's
 * IsAdminRole permission on writes.
 */
export async function setOrgAnnualSubscription(id, annualPgk) {
  const payload = {
    annual_subscription_pgk:
      annualPgk == null || annualPgk === '' ? null : String(annualPgk),
  }
  const { data } = await api.patch(`/api/organizations/${id}/`, payload)
  return data
}

/**
 * Upload (or replace) an organisation's logo. Platform-admin only —
 * the OrganizationViewSet permission gate is `IsAdminRole` for writes.
 * Pass `null` to clear.
 *
 * Same shape as `setDrugImage` / `setLabTestImage` / etc. — multipart
 * PATCH so we don't have to send the rest of the org fields.
 */
export async function setOrganizationLogo(id, file) {
  const fd = new FormData()
  if (file) {
    fd.append('logo', file)
  } else {
    fd.append('logo', '')
  }
  const { data } = await api.patch(`/api/organizations/${id}/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// ----- Staff links (enrolled members) -----

export async function listOrgStaffLinks({ orgId, search = '', isActive } = {}) {
  // Callers (the screening "Screen a patient" picker and the org-dashboard
  // roster) render the WHOLE roster client-side — there is no pagination
  // control. Without an explicit page_size the API falls back to DRF's
  // default of 50, and unwrapList only returns the first page: any org with
  // >50 active staff silently loses the overflow. Because rows are ordered by
  // ascending patient_id, the people dropped are the most recently imported —
  // so freshly roster-imported staff vanish from the picker while still being
  // findable in the all-patients search. Request the full roster instead.
  // (PNG org rosters are at most a few hundred people; 1000 covers every real
  // org with headroom, and the server caps page_size there anyway.)
  const params = { page_size: 1000 }
  if (orgId) params.organization = orgId
  if (search?.trim()) params.search = search.trim()
  if (isActive != null) params.is_active = isActive
  const { data } = await api.get('/api/organizations/staff-links/', { params })
  return unwrapList(data)
}

export async function updateOrgStaffLink(id, patch) {
  const { data } = await api.patch(`/api/organizations/staff-links/${id}/`, patch)
  return data
}

/**
 * Soft-delete: server-side perform_destroy flips is_active off rather
 * than hard-deleting (preserves the encounter trail). Platform admins
 * can hard-delete via Django admin.
 */
export async function removeOrgStaffLink(id) {
  await api.delete(`/api/organizations/staff-links/${id}/`)
}

/**
 * Bulk roster import — two-step (analyse, then apply). Multipart POST with
 * `file` (CSV), `organization_id`, and `dry_run`.
 *
 * Pass dryRun=true (the default) to analyse only — the server writes
 * nothing and returns what the import WOULD do. Pass dryRun=false to
 * commit. Response shape is identical for both:
 *   {
 *     dry_run, rows_processed,
 *     counts: { create, link, update, error },
 *     rows: [{ row, name, email, employee_id,
 *              action, matched_by, error, note }, ...],
 *   }
 *
 * CSV columns (header row required):
 *   first_name, last_name             — required
 *   email and/or employee_id          — at least one per row; employee_id
 *                                       is required when a row has no email
 *   phone, department, role,          — optional
 *   centre, notes
 *   date_of_birth                     — optional; YYYY-MM-DD or DD/MM/YYYY.
 *                                       Drives the age-based screening /
 *                                       fitness-for-work assessment.
 *   sex                               — optional; male / female / other
 */
export async function bulkUploadOrgRoster({ file, orgId, dryRun = true }) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('organization_id', String(orgId))
  fd.append('dry_run', dryRun ? 'true' : 'false')
  const { data } = await api.post(
    '/api/organizations/staff-links/bulk_upload/', fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

// ----- Admin links (co-administrators) -----

export async function listOrgAdminLinks({ orgId } = {}) {
  const params = {}
  if (orgId) params.organization = orgId
  const { data } = await api.get('/api/organizations/admin-links/', { params })
  return unwrapList(data)
}

// ----- Co-admin invitations (StaffInvitation kind=org_admin) -----

export async function listCoAdminInvitations({ orgId } = {}) {
  const params = { target_role: 'org_admin' }
  if (orgId) params.target_organization = orgId
  const { data } = await api.get('/api/staff-invitations/', { params })
  return unwrapList(data)
}

/**
 * Invite a co-administrator for an organisation. Backend validates that
 * the request user is the primary admin of the target org and that
 * target_role is 'org_admin'. The accept flow at
 * /accept-invite/staff/:token wires up an OrganizationAdminLink at
 * accept-time.
 */
export async function inviteCoAdmin({ orgId, email, firstName, lastName, isPrimary = false, note = '' }) {
  const body = {
    target_role: 'org_admin',
    target_organization: orgId,
    email,
    first_name: firstName,
    last_name: lastName,
    is_admin: Boolean(isPrimary),
    note,
  }
  const { data } = await api.post('/api/staff-invitations/', body)
  return data
}

export async function revokeCoAdminInvitation(id) {
  const { data } = await api.post(`/api/staff-invitations/${id}/revoke/`)
  return data
}

export async function resendCoAdminInvitation(id) {
  const { data } = await api.post(`/api/staff-invitations/${id}/resend/`)
  return data
}

// ----- Service coverage config (value/billing Phase 2) -----

/**
 * ICER platform-admin only. The four conditional services (lab, medication,
 * radiology, ambulance) with their EFFECTIVE coverage classification for one
 * org — a per-org override wins over the inherited platform default. Returns
 * { organization, organization_name, services: [{ service_type,
 * service_type_label, coverage_mode, source, rate_id, markup_pct, ... }] }.
 */
export async function getServiceCoverageMatrix(orgId) {
  const { data } = await api.get(
    '/api/organizations/service-rates/coverage_matrix/',
    { params: { organization: orgId } },
  )
  return data
}

/**
 * ICER platform-admin only. Upsert one conditional service's coverage_mode
 * for an org. The first time, the server creates a per-org ServiceRate row
 * cloning the platform default's pricing. mode ∈
 *   'covered_in_subscription' | 'billable_addon' | 'excluded'.
 */
export async function setServiceCoverage({ orgId, serviceType, mode }) {
  const { data } = await api.post(
    '/api/organizations/service-rates/set_coverage/',
    { organization: orgId, service_type: serviceType, coverage_mode: mode },
  )
  return data
}

// ----- Value & Billing statements (value/billing Phase 3) -----

/** ICER-admin only. Assemble (NOT save) the tagged statement for an org +
 *  period — feeds the editable preview. ISO dates 'YYYY-MM-DD'. */
export async function previewOrgStatement({ orgId, periodStart, periodEnd }) {
  const { data } = await api.post('/api/organizations/statements/preview/', {
    organization: orgId, period_start: periodStart, period_end: periodEnd,
  })
  return data
}

/** ICER-admin only. Freeze + persist a statement (renders the PDF). Pass an
 *  edited `snapshot` to issue the previewed/adjusted figures; omit to
 *  re-assemble fresh on the server. */
export async function issueOrgStatement({ orgId, periodStart, periodEnd, snapshot }) {
  const body = { organization: orgId, period_start: periodStart, period_end: periodEnd }
  if (snapshot) body.snapshot = snapshot
  const { data } = await api.post('/api/organizations/statements/issue/', body)
  return data
}

/** Issued statements. Admin sees all (optionally one org); org admin sees
 *  only their own. */
export async function listOrgStatements(orgId) {
  const params = { page_size: 200 }
  if (orgId) params.organization = orgId
  const { data } = await api.get('/api/organizations/statements/', { params })
  return unwrapList(data)
}

/** Download an issued statement PDF as a Blob (JWT-gated endpoint). */
export async function downloadOrgStatement(id) {
  const res = await api.get(
    `/api/organizations/statements/${id}/download/`, { responseType: 'blob' })
  return res.data
}
